import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  AveragePlayerScore,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconTrash } from '@sorare/core/src/atoms/icons/system/iconTrash.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import type { TotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';
import { cart } from '@sorare/core/src/lib/glossary';
import {
  ClickAddToCart_Origin,
  ClickRemoveFromCart_Origin,
} from '@sorare/core/src/protos/events/so5/shared/events';

import { AnyPlayerStatsIcon } from '#src/components/player/AnyPlayerStatsIcon/index.tsx';
import { FlexCard } from '#src/components/token/FlexCard/index.tsx';
import { useCartEvents } from '#src/hooks/events/useCartEvents.ts';
import { useCart } from '#src/hooks/useCart.ts';

import type {
  SavedForLaterItem_anyCard,
  SavedForLaterItem_cartItem,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 0.5 })`
  max-width: 156px;
  width: 100%;
`;
const Metas = styled(Vertical).attrs({ gap: 0.5 })`
  flex-grow: 1;
`;
const CardWrapper = styled.div`
  width: 100%;
`;
const Gap = styled.div`
  height: var(--triple-unit);
`;

const Actions = styled(Vertical)`
  font: var(--t-label-m);
  color: var(--c-nd-600);
`;

type Props = {
  cartItem: Nullable<SavedForLaterItem_cartItem>;
  card: Nullable<SavedForLaterItem_anyCard>;
  onStatsClick: () => void;
};

export const SavedForLaterItem = ({ cartItem, card, onStatsClick }: Props) => {
  const { addCartItemsToCart, removeFromCart } = useCart();
  const { trackClickAddToCart, trackClickRemoveFromCart } = useCartEvents({
    anyCard: card,
  });
  const sport = card?.sport || Sport.FOOTBALL;

  const modePerSport: Record<Sport, TotalAveragePlayerScore> = {
    [Sport.FOOTBALL]: AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
    [Sport.NBA]: AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
    [Sport.BASEBALL]: AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
  };
  const mode = modePerSport[sport];
  if (!cartItem || !card) return null;

  return (
    <Root>
      <CardWrapper>
        <FlexCard card={card} width={160} />
      </CardWrapper>
      <Metas>
        <Horizontal>
          <AnyPlayerStatsIcon player={card?.anyPlayer} onClick={onStatsClick} />
          <PlayerScore small player={card.anyPlayer} mode={mode} />
        </Horizontal>
        {cartItem.price ? (
          <AmountWithConversion monetaryAmount={cartItem.price} />
        ) : (
          <Gap />
        )}
        <Actions>
          <Button
            color="quaternary"
            size="small"
            disabled={!cartItem.price}
            onClick={async () => {
              trackClickAddToCart(ClickAddToCart_Origin.CART_SAVED_FOR_LATER);
              return addCartItemsToCart.mutate([cartItem.id], card.sport);
            }}
          >
            <FormattedMessage
              {...(cartItem.price ? cart.addToCart : cart.soldOut)}
            />
          </Button>
          <Button
            color="tertiary"
            size="small"
            onClick={async () => {
              trackClickRemoveFromCart(
                ClickRemoveFromCart_Origin.CART_SAVED_FOR_LATER
              );
              return removeFromCart.mutate([cartItem.id], card.sport);
            }}
            aria-label="Remove"
          >
            <FontAwesomeIcon icon={iconTrash} />
          </Button>
        </Actions>
      </Metas>
    </Root>
  );
};

SavedForLaterItem.fragments = {
  anyCard: gql`
    fragment SavedForLaterItem_anyCard on AnyCardInterface {
      slug
      anyPlayer {
        slug
        displayName
        ...AnyPlayerStatsIcon_anyPlayer
        ...PlayerScore_anyPlayer
      }
      ...FlexCard_anyCard
      ...useCartEvents_anyCard
    }
    ${useCartEvents.fragments.anyCard}
    ${FlexCard.fragments.anyCard}
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
    ${PlayerScore.fragments.anyPlayer}
  ` as TypedDocumentNode<SavedForLaterItem_anyCard>,
  cartItem: gql`
    fragment SavedForLaterItem_cartItem on CartItem {
      id
      price {
        ...AmountWithConversion_monetaryAmount
      }
    }
    ${AmountWithConversion.fragments.monetaryAmount}
  ` as TypedDocumentNode<SavedForLaterItem_cartItem>,
};
